<i18n lang="yaml" locale="en">
  modals: Modals
  add modal: Add modal
  slides: Slides
  edit slides: Configure navigation
  smooth slider: Smooth slider
  edit smooth slider: Configure smooth slider
  idle: Screen saver
  open idle: Open screen saver
  edit idle: Configure screen saver
  attention: Attention!
  confirm delete modals: All modals will be deleted. Continue?
  confirm delete modal content: The content of this modal will be deleted. Continue?
  confirm delete slides: All slides will be deleted. Continue?
  name: Name
  enter modal name: Please enter the modal name.
  copy of: Copy of {title}
  soft limit modals reached: The recommenden number of modals ({limit}) is exceeded...
  smooth slider and slides error: "Slides and smooth slider can't be used at the same time."
  locales: Languages
  add locale: Add language
  enter locale code (2 digits): "Please enter the two-digit language code (ISO 639-1). Examples: DE=german, EN=english, ..."
  enter default locale (2 digits): "Please enter the two-digit language code for the default language (ISO 639-1). Examples: DE=german, EN=english, ..."
  dupliate locale: This language already exists.
  locale code: Language code
  (default): " (Default)"
  confirm delete locales: All languages will be deleted. Continue?
  confirm delete locale: The language will be deleted. Continue?
  settings: Settings
</i18n>

<i18n lang="yaml" locale="de">
  modals: Modals
  add modal: Modal hinzufügen
  slides: Seiten
  edit slides: Navigation einstellen
  smooth slider: Stufenlose Seite
  edit smooth slider: Stufenlose Seite einstellen
  idle: Bildschirmschoner
  open idle: Bildschirmschoner öffnen
  edit idle: Bildschirmschoner einstellen
  attention: Achtung!
  confirm delete modals: Alle Modals werden gelöscht. Fortfahren?
  confirm delete modal content: Der Inhalt dieses Modals wird gelöscht. Fortfahren?
  confirm delete slides: Alle Seiten werden gelöscht. Fortfahren?
  name: Name
  enter modal name: Bitte geben Sie den Namen des Modals an.
  copy of: Kopie von {title}
  soft limit modals reached: Die empfohlene Anzahl an Modals ({limit}) ist überschritten...
  smooth slider and slides error: "Seiten und stufenlose Seiten können nicht gleichzeitig verwendet werden."
  locales: Sprachen
  add locale: Sprache hinzufügen
  enter locale code (2 digits): "Bitte geben Sie den zweistelligen Sprachcode an (ISO 639-1). Beispiele: DE=deutsch, EN=englisch, ..."
  enter default locale (2 digits): "Bitte geben Sie den zweistelligen Sprachcode der Standardsprache an (ISO 639-1). Beispiele: DE=deutsch, EN=englisch, ..."
  duplicate locale: Diese Sprache existiert bereits.
  locale code: Sprachcode
  (default): " (Standard)"
  confirm delete locales: Alle Sprachen werden gelöscht. Fortfahren?
  confirm delete locale: Die Sprache wird gelöscht. Fortfahren?
  settings: Einstellungen
</i18n>

<template>
  <div class="editor menu-wrapper">
    <div
      class="menu"
      :class="{active}"
    >
      <div
        class="slide-icon"
        @click="toggleSelf"
      >
        <chevron-circle
          size="s"
          color="success"
          :active="active"
        />
      </div>
      <div class="menu-heading">
        <span class="project-title">
          {{ $parent.$props.name }}
        </span>
        <import-project />
        <settings
          size="xs"
          @click="togglePropEditor"
        />
      </div>
      <div class="menu-item-heading">
        <h4>{{ $t('locales') }}</h4>
        <label class="checkbox">
          <input
            type="checkbox"
            ref="localesToggle"
            :checked="defaultLocale"
            @change="toggleLocales($event.target.checked)"
          >
          <span class="toggle" />
        </label>
      </div>
      <div
        class="menu-item-content"
        v-if="defaultLocale"
      >
        <ul class="locale-list scroll-y">
          <li
            v-for="locale in [defaultLocale].concat($locales)"
            :key="locale"
            :class="{ active: isContentLocale(locale) }"
          >
            <lang-flag
              class="lang-flag"
              :iso="locale"
              :squared="false"
            />
            <span
              class="locale-name"
              @click="setContentLocale(locale)"
            >
              {{ $te(locale) ? $t(locale) : translateLangIso(locale) }}
              <template
                v-if="locale === defaultLocale"
              >
                {{ $t('(default)') }}
              </template>
            </span>
            <delete
              v-if="locale !== defaultLocale"
              size="xs"
              color="danger"
              class="icon delete"
              @click="removeLocale(locale)"
            />
          </li>
        </ul>
        <tw-button
          icon="add-circle"
          color="main"
          class="btn-add-locale"
          block
          @click="openNewLocalePrompt"
        >
          {{ $t('add locale') }}
        </tw-button>
      </div>
      <div class="menu-item-heading">
        <h4>{{ $t('modals') }}</h4>
        <label
          v-if="!$localeMode"
          class="checkbox"
        >
          <input
            type="checkbox"
            ref="modalControllerToggle"
            :checked="$modals"
            @change="toggleModalController($event.target.checked)"
          >
          <span class="toggle" />
        </label>
      </div>
      <div
        class="menu-item-content"
        v-if="$modals"
      >
        <ul class="modal-list scroll-y">
          <li
            v-for="modal in $modals.getters.children"
            :key="modal.getters.name"
          >
            <span
              class="modal-name"
              @click="openModal(modal)"
            >
              {{ modal.getters.props && modal.getters.props.title }}
            </span>
            <settings
              size="xs"
              class="icon"
              @click="editModal(modal)"
            />
            <copy
              v-if="!$localeMode"
              size="xs"
              class="icon"
              @click="cloneModal(modal)"
            />
            <delete
              v-if="!$localeMode"
              size="xs"
              color="danger"
              class="icon delete"
              @click="deleteModal(modal)"
            />
          </li>
        </ul>
        <tw-button
          v-if="!$localeMode"
          icon="add-circle"
          color="main"
          class="btn-add-modal"
          block
          @click="openNewModalPrompt"
        >
          {{ $t('add modal') }}
        </tw-button>
      </div>
      <div class="menu-item-heading">
        <h4>
          {{ $t('slides') }}
          <span
            v-if="$slider"
            class="edit-link"
            @click="openSliderSettings"
          >
            ({{ $t('settings') }})
          </span>
        </h4>
        <label
          v-if="!$localeMode"
          class="checkbox"
        >
          <input
            type="checkbox"
            ref="sliderToggle"
            :checked="$slider"
            @change="toggleSlider($event.target.checked)"
          >
          <span class="toggle" />
        </label>
      </div>
      <div class="menu-item-heading">
        <h4>
          {{ $t('smooth slider') }}
          <span
            v-if="$smoothSlider"
            class="edit-link"
            @click="openSmoothSliderSettings"
          >
            ({{ $t('settings') }})
          </span>
        </h4>
        <label
          v-if="!$localeMode"
          class="checkbox"
        >
          <input
            type="checkbox"
            ref="smoothSliderToggle"
            :checked="$smoothSlider"
            @change="toggleSmoothSlider($event.target.checked)"
          >
          <span class="toggle" />
        </label>
      </div>
      <div class="menu-item-heading">
        <h4>
          {{ $t('idle') }}
          <span
            v-if="$idle"
            class="edit-link"
            @click="openIdleSettings"
          >
            ({{ $t('settings') }})
          </span>
        </h4>
        <label
          v-if="!$localeMode"
          class="checkbox"
        >
          <input
            type="checkbox"
            ref="idleToggle"
            :checked="$idle"
            @change="toggleIdle($event.target.checked)"
          >
          <span class="toggle" />
        </label>
      </div>
      <div
        class="menu-item-content"
        v-if="$idle"
      >
        <tw-button
          icon="open"
          color="main"
          class="btn-idle-open"
          block
          @click="openIdle"
        >
          {{ $t('open idle') }}
        </tw-button>
      </div>
    </div>
  </div>
</template>

<script>
import Delete from '@/components/icons/Delete'
import ChevronCircle from '@/components/icons/ChevronCircle'
import Settings from '@/components/icons/Settings'
import Copy from '@/components/icons/Copy'
import TwButton from '@/components/ui/TwButton'
import { Confirm, Prompt, Error, Warning } from '@/plugins/snotify'
import softLimits from '@/projectSoftLimits'
import ImportProject from '@/components/editor/ImportProject'
import { DuplicateLocale } from '@/errors'
import langs from '@cospired/i18n-iso-languages'
import LangFlag from 'vue-lang-code-flags'

// Support german & english languages for iso language code translations
langs.registerLocale(require('@cospired/i18n-iso-languages/langs/en.json'))
langs.registerLocale(require('@cospired/i18n-iso-languages/langs/de.json'))

export default {
  name: 'ProjectEditor',
  inheritAttrs: false,
  components: {
    Delete,
    ChevronCircle,
    Settings,
    Copy,
    TwButton,
    ImportProject,
    LangFlag
  },
  data () {
    return {
      active: false
    }
  },
  computed: {
    $modals () {
      return this.$store && this.$store.getters.childrenByName.modals
    },
    $slider () {
      return this.$store && this.$store.getters.childrenByName.slider
    },
    $smoothSlider () {
      return this.$store && this.$store.getters.childrenByName.smoothSlider
    },
    $idle () {
      return this.$store && this.$store.getters.childrenByName.idle
    },
    $locales () {
      return this.$store && this.$store.getters.locales
    },
    defaultLocale () {
      return this.$store && this.$store.getters.defaultLocale
    },
    contentLocale () {
      return this.$store && this.$root.$store.getters.contentLocale
    },
    activeLocale () {
      return this.contentLocale === '' ? this.defaultLocale : this.contentLocale
    },
    $localeMode () {
      return this.$parent.$localeMode
    }
  },
  methods: {
    translateLangIso (locale) {
      return langs.getName(locale, this.$root.$store.getters.uiLocale) || locale
    },
    isValidIso (locale) {
      return langs.isValid(locale)
    },
    toggleSelf () {
      this.active = !this.active
    },
    togglePropEditor () {
      this.$router.replace({ query: { edit: this.$store.getters.path } })
    },
    toggleLocales (newState) {
      if (newState) {
        // HACK: to prevent checkbox from changing state before action is confirmed/canceled
        this.$refs.localesToggle.checked = false
        this.openSetDefaultLocalePrompt()
      } else {
        // HACK: to prevent checkbox from changing state before action is confirmed/canceled
        this.$refs.localesToggle.checked = true
        const dispatchRemove = () => {
          this.setContentLocale('')
          this.$store.dispatch('resetLocales')
        }
        if (this.$locales.length > 0) {
          new Confirm(this.$t('attention'), this.$t('confirm delete locales'), dispatchRemove).open()
        } else {
          dispatchRemove()
        }
      }
    },
    openSetDefaultLocalePrompt () {
      new Prompt(this.$t('locale code'), this.$t('enter default locale (2 digits)'), this.setDefaultLocale).open()
    },
    openNewLocalePrompt () {
      new Prompt(this.$t('locale code'), this.$t('enter locale code (2 digits)'), this.addLocale).open()
    },
    addLocale (locale) {
      try {
        this.$store.dispatch('addLocale', { locale })
      } catch (e) {
        switch (e.constructor) {
          case DuplicateLocale:
            new Error(this.$t('duplicate locale')).open()
            break
          default:
            throw e
        }
      }
    },
    setDefaultLocale (locale) {
      this.$refs.localesToggle.checked = true
      this.$store.dispatch('setDefaultLocale', { locale })
    },
    removeLocale (locale) {
      const dispatchRemove = () => {
        this.setContentLocale('')
        this.$store.dispatch('removeLocale', { locale })
      }
      new Confirm(this.$t('attention'), this.$t('confirm delete locale'), dispatchRemove).open()
    },
    setContentLocale (locale) {
      this.$root.$store.dispatch('setContentLocale', {
        locale: locale === this.$store.getters.defaultLocale ? '' : locale
      })
    },
    isContentLocale (locale) {
      return this.contentLocale ? this.contentLocale === locale : this.defaultLocale === locale
    },
    toggleModalController (newState) {
      if (newState) {
        this.$store.dispatch('addChild', {
          childType: 'modalController',
          name: 'modals'
        })
      } else {
        // HACK: to prevent checkbox from changing state before action is confirmed/canceled
        this.$refs.modalControllerToggle.checked = true
        const dispatchRemove = () => {
          this.$store.dispatch('removeChild', {
            name: 'modals'
          })
        }
        if (this.$modals.getters.numChildren > 0) {
          new Confirm(this.$t('attention'), this.$t('confirm delete modals'), dispatchRemove).open()
        } else {
          dispatchRemove()
        }
      }
    },
    openNewModalPrompt () {
      new Prompt(this.$t('name'), this.$t('enter modal name'), this.addModal).open()
    },
    addModal (newModalName) {
      this.$modals.dispatch('addChild', {
        childType: 'modal',
        props: {
          title: newModalName
        }
      }).then((modal) => {
        // register link
        this.$root.$store.dispatch('registerLink', {
          group: 'modal',
          titleFunc: () => modal.getters.props.title,
          href: modal.getters.path
        })
        // check if soft limit is reached
        if (this.$modals.getters.numChildren > softLimits.modals) {
          new Warning('soft limit modals reached', { limit: softLimits.modals }).open()
        }
      })
    },
    deleteModal (modal) {
      const dispatch = () => {
        // unregister link
        this.$root.$store.dispatch('unregisterLink', {
          group: 'modal',
          href: modal.getters.path
        })
        // remove modal
        this.$modals.dispatch('removeChild', {
          name: modal.getters.name
        })
      }
      if (modal.getters.numChildren > 0) {
        new Confirm(this.$t('attention'), this.$t('confirm delete modal content'), dispatch).open()
      } else {
        dispatch()
      }
    },
    openModal (modal) {
      this.$router.push(modal.getters.path)
    },
    editModal (modal) {
      this.$router.push({
        path: modal.getters.path,
        query: { edit: modal.getters.path }
      })
    },
    cloneModal (modal) {
      this.$modals.dispatch('cloneChild', {
        name: modal.getters.name,
        props: {
          title: this.$t('copy of', { title: modal.getters.props.title })
        }
      }).then((newModal) => {
        // register link
        this.$root.$store.dispatch('registerLink', {
          group: 'modal',
          title: newModal.getters.props.title,
          href: newModal.getters.path
        })
        // check if soft limit is reached
        if (this.$modals.getters.numChildren > softLimits.modals) {
          new Warning('soft limit modals reached', { limit: softLimits.modals }).open()
        }
      })
    },
    toggleSlider (newState) {
      if (newState) {
        if (!this.$smoothSlider) {
          this.$store.dispatch('addChild', {
            childType: 'slider',
            name: 'slider'
          })
          this.$router.push({ path: this.$slider.getters.path })
        } else {
          new Error(this.$t('smooth slider and slides error')).open()
          this.$refs.sliderToggle.checked = false
        }
      } else {
        // HACK: to prevent checkbox from changing state before action is confirmed/canceled
        this.$refs.sliderToggle.checked = true
        const dispatchRemove = () => {
          this.$store.dispatch('removeChild', {
            name: 'slider'
          })
        }
        if (this.$slider.getters.numChildren > 0) {
          new Confirm(this.$t('attention'), this.$t('confirm delete slides'), dispatchRemove).open()
        } else {
          dispatchRemove()
        }
      }
    },
    toggleSmoothSlider (newState) {
      if (newState) {
        if (!this.$slider) {
          this.$store.dispatch('addChild', {
            childType: 'smoothSlider',
            name: 'smoothSlider'
          })
          this.$router.push({ path: this.$smoothSlider.getters.path })
        } else {
          new Error(this.$t('smooth slider and slides error')).open()
          this.$refs.smoothSliderToggle.checked = false
        }
      } else {
        // HACK: to prevent checkbox from changing state before action is confirmed/canceled
        this.$refs.smoothSliderToggle.checked = true
        const dispatchRemove = () => {
          this.$store.dispatch('removeChild', {
            name: 'smoothSlider'
          })
        }
        if (this.$smoothSlider.getters.numChildren > 0) {
          new Confirm(this.$t('attention'), this.$t('confirm delete slides'), dispatchRemove).open()
        } else {
          dispatchRemove()
        }
      }
    },
    openSliderSettings () {
      this.$router.replace({ query: { edit: this.$slider.getters.path } })
    },
    openSmoothSliderSettings () {
      this.$router.replace({ query: { edit: this.$smoothSlider.getters.path } })
    },
    toggleIdle (newState) {
      if (newState) {
        this.$store.dispatch('addChild', {
          childType: 'idle',
          name: 'idle'
        })
      } else {
        // HACK: to prevent checkbox from changing state before action is confirmed/canceled
        this.$refs.idleToggle.checked = true
        const dispatchRemove = () => {
          this.$store.dispatch('removeChild', {
            name: 'idle'
          })
        }
        if (this.$idle.getters.numChildren > 0) {
          new Confirm(this.$t('attention'), this.$t('confirm delete slides'), dispatchRemove).open()
        } else {
          dispatchRemove()
        }
      }
    },
    openIdleSettings () {
      this.$router.replace({ query: { edit: this.$idle.getters.path } })
    },
    openIdle () {
      this.$router.push({ path: this.$idle.getters.path })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/uiColors';

.checkbox {
  position:relative;
  cursor: pointer;

  input {
    display: none;

    &:checked ~ .toggle {
      background-color: rgba($color: #fff, $alpha: 1);

      &::after {
        display: block;
      }
    }
  }

  .toggle {
    position: absolute;
    top: 0;
    right: 0;
    height: 16px;
    width: 16px;
    border: 2px solid #fff;
    border-radius: 3px;
    transition: all 250ms ease-in-out;

    &:after {
      content: "";
      position: absolute;
      display: none;
      left: 3px;
      top: 0;
      width: 6px;
      height: 11px;
      border: solid $color-main-darker;
      border-width: 0 3px 3px 0;
      transform: rotate(40deg);
    }
  }

  &:hover input ~ .toggle {
    background-color: rgba($color: #fff, $alpha: 1);
  }
}

.lang-flag {
  margin-right: 5px;
}

.modal-list,
.locale-list {
  margin: 20px 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    border-bottom: 1px solid rgba(255, 255, 255, 0.25);
    padding: 7px 0;

    &:last-child {
      border-bottom: none;
    }

    .modal-name,
    .locale-name {
      margin-right: auto;

      &:hover {
        cursor: pointer;
        color: rgba($color: #fff, $alpha: 0.75);
      }
    }

    &.active {
      .locale-name {
        font-weight: 600;
      }
    }

    .flag-icon {
      width: 15px;
      margin-right: 10px;
      margin-top: -3px;
    }

    .icon {
      margin-left: 8px;

      &.delete {
        margin-left: 16px;
      }
    }
  }
}

.locale-dropdown {
  position: absolute;
  top: 3px;
  left: 4px;
  z-index: z(projectEditor);
  opacity: 0.7;
}

.menu {
  width: 380px;
  height: 100vh;
  background-color: rgba($color: $color-main-darker, $alpha: 0.98);
  position: fixed;
  left: -380px;
  top: 0px;
  transition: transform 250ms ease-in-out;
  padding: 30px 20px;
  font-size: 14px;
  letter-spacing: 0.25pt;
  font-weight: 400;
  box-shadow: 0 0 20px rgba(#000, 0.5);
  z-index: z(projectEditor);

  &.active {
    transform: translateX(380px);
  }

  .menu-heading {
    display: flex;
    margin: -30px -20px 30px -20px;
    padding: 20px;
    background-color: darken($color-main-darker, 5);
    font-weight: 600;
    text-transform: uppercase;

    .project-title {
      color: $color-main;
    }
  }

  .slide-icon {
    display: block;
    position: absolute;
    padding: 8px;
    top: 50%;
    right: -40px;
    background-color: rgba($color: $color-main-darker, $alpha: 0.98);
    transition: 250ms ease-in-out;
    transform: translateY(-50%);
    line-height: 0;
    border-radius: 0 50% 50% 0;
  }

  .menu-item-heading {
    display: flex;
    justify-content: space-between;
    margin-top: 24px;

    h4 {
      margin: 0;
      font-size: 16px;
      font-weight: 600;

      .edit-link {
        font-weight: normal;
        color: $color-main;
        cursor: pointer;
        letter-spacing: 0;
        font-size: 0.95rem;

        &:hover {
          opacity: 0.5;
        }
      }
    }
  }

  .menu-item-content {
    margin-bottom: 50px;

    ul {
      max-height: 38vh;
    }
  }

  .btn-add-modal,
  .btn-add-locale,
  .btn-slider-settings,
  .btn-idle-open,
  .btn-idle-settings {
    margin-top: 20px;
    justify-content: flex-start;
  }

  .btn-idle-settings {
    margin-top: 0.25rem;
  }
}
</style>
